/**
 * BaseReportApplication - Abstract base class for read-only report applications
 *
 * Reports differ from editing apps: data is written externally to entity.skeleton
 * as JSON and displayed read-only. No Y.js CRDT is needed.
 *
 * Subclasses define the report type and typed data accessors.
 */

import { BaseApplication } from '../baseApplication/BaseApplication';
import { AppConfig } from '../baseApplication/types';
import { entityService } from '../entityService';
import { BaseReportData, ReportConfig, ReportState } from './types';

export abstract class BaseReportApplication<T extends BaseReportData> extends BaseApplication {
  protected reportData: T | null = null;
  private dataChangeHandlers: Set<() => void> = new Set();
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    super();
    this.autoSaveEnabled = false;
    this.suggestionMode = false;
  }

  /**
   * Get the report-specific configuration (poll interval, display name, etc.)
   */
  protected abstract getReportConfig(): ReportConfig;

  /**
   * Determine whether the given skeleton data is terminal (no more polling needed).
   * Default: stops when state is 'complete' or 'failed'.
   */
  protected isTerminalState(data: T): boolean {
    return data.state === 'complete' || data.state === 'failed';
  }

  /**
   * Load report from backend.
   * Reads entity.skeleton directly — no Y.js.
   */
  async load(entityId: string, _options: { offline?: boolean } = {}): Promise<void> {
    console.log(`[${this.getReportConfig().reportType}] === LOADING REPORT ===`, entityId);

    await this.initialize(entityId);

    await this.refreshFromBackend();

    // Poll for live updates while report is still running
    if (this.reportData && !this.isTerminalState(this.reportData)) {
      this.setupPolling();
    }

    console.log(`[${this.getReportConfig().reportType}] === REPORT LOAD COMPLETE ===`);
  }

  /**
   * Fetch the latest skeleton from the backend and notify listeners.
   */
  async refreshFromBackend(): Promise<void> {
    if (!this.entityId) return;

    try {
      const entity = await entityService.get(this.entityId);
      if (!entity) {
        console.warn(`[BaseReportApplication] Entity not found: ${this.entityId}`);
        return;
      }

      this.reportData = (entity.skeleton as T) ?? null;
      this.notifyDataChange();

      // Stop polling when report reaches a terminal state
      if (this.reportData && this.isTerminalState(this.reportData)) {
        this.stopPolling();
      }
    } catch (error) {
      console.error('[BaseReportApplication] Failed to refresh from backend:', error);
    }
  }

  /**
   * Get the current report state.
   */
  getReportState(): ReportState {
    return (this.reportData?.state as ReportState) ?? 'pending';
  }

  /**
   * Get the raw report data (typed by subclass).
   */
  getData(): T | null {
    return this.reportData;
  }

  /**
   * Subscribe to data changes. Returns an unsubscribe function.
   */
  onDataChange(handler: () => void): () => void {
    this.dataChangeHandlers.add(handler);
    return () => {
      this.dataChangeHandlers.delete(handler);
    };
  }

  /**
   * Start polling the backend for updates.
   */
  private setupPolling(): void {
    if (this.pollIntervalId !== null) return;

    const intervalMs = this.getReportConfig().pollIntervalMs;
    console.log(`[BaseReportApplication] Starting poll every ${intervalMs}ms`);

    this.pollIntervalId = setInterval(async () => {
      await this.refreshFromBackend();
    }, intervalMs);
  }

  /**
   * Stop polling.
   */
  private stopPolling(): void {
    if (this.pollIntervalId !== null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
      console.log('[BaseReportApplication] Polling stopped (terminal state)');
    }
  }

  private notifyDataChange(): void {
    this.dataChangeHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('[BaseReportApplication] Data change handler error:', error);
      }
    });
  }

  /**
   * Clean up polling and handlers on destroy.
   */
  async destroy(): Promise<void> {
    this.stopPolling();
    this.dataChangeHandlers.clear();
    await super.destroy();
  }

  // -------------------------------------------------------------------------
  // AI / entity_read integration
  // -------------------------------------------------------------------------

  /**
   * Override BaseApplication's Y.js-based handleEntityRead.
   * Reports store data in entity.skeleton, not Y.js fragments, so we build
   * virtual nodes from this.reportData and apply the selector manually.
   */
  async handleEntityRead(selector: any, _options: any = {}): Promise<any> {
    if (!this.reportData) {
      await this.refreshFromBackend();
    }

    if (!this.reportData) {
      return { nodes: [], wordCount: 0, stats: { nodeCount: 0, queryTime: 0 } };
    }

    const startTime = performance.now();
    const allNodes = this.serializeReportNodes();

    let filtered: any[];
    if (selector.nodeId) {
      filtered = allNodes.filter(n => n.id === selector.nodeId);
    } else if (selector.nodeIds) {
      const idSet = new Set<string>(selector.nodeIds);
      filtered = allNodes.filter(n => idSet.has(n.id));
    } else if (selector.className) {
      filtered = allNodes.filter(n => n.className === selector.className);
    } else if (selector.query) {
      const q = (selector.query as string).toLowerCase();
      filtered = allNodes.filter(n => (n.text as string).toLowerCase().includes(q));
    } else if (selector.range) {
      const start = selector.range.start ?? 0;
      const limit = selector.range.limit ?? allNodes.length;
      filtered = allNodes.slice(start, start + limit);
    } else {
      filtered = allNodes;
    }

    const wordCount = filtered.reduce(
      (acc, n) => acc + ((n.text as string)?.split(/\s+/).filter(Boolean).length ?? 0),
      0
    );

    return {
      nodes: filtered,
      wordCount,
      stats: { nodeCount: filtered.length, queryTime: performance.now() - startTime },
    };
  }

  /**
   * Build the node list that represents this report's data for AI consumption.
   * Default serializes the entire skeleton as a single JSON node.
   * Subclasses should override this for richer, more granular structure.
   */
  protected serializeReportNodes(): any[] {
    const config = this.getReportConfig();
    const text = JSON.stringify(this.reportData, null, 2);
    return [
      {
        id: `${config.reportType}_data`,
        className: 'report_data',
        text,
        attrs: { data: this.reportData },
      },
    ];
  }

  // -------------------------------------------------------------------------
  // AppConfig required by BaseApplication
  // -------------------------------------------------------------------------

  protected getConfig(): AppConfig {
    const reportConfig = this.getReportConfig();
    return {
      appId: reportConfig.reportType,
      appName: reportConfig.displayName,
      containerType: 'report',
      layoutMode: 'report',
      supportedClasses: {},
      supportedPositionTypes: ['flow'],
      supportsDragDrop: false,
      supportsResize: false,
      supportsRotation: false,
      editorSelector: `.${reportConfig.reportType}-app`,
    };
  }
}
